/** How a reminder is delivered — mirrors Google Calendar's reminder methods. */
export type ReminderMethod = 'popup' | 'email';

/** A single reminder notification, stored as Google stores it: a method + minutes before. */
export interface Reminder {
  method: ReminderMethod;
  minutes: number;
}

export interface ReminderPickerProps {
  label?: string;
  /** Help text shown under the label. */
  hint?: string;
  value: Reminder[];
  onChange: (value: Reminder[]) => void;
  /** Largest reminder allowed, in minutes (default 28 days). */
  maxMinutes?: number;
  className?: string;
}

type Unit = 'minutes' | 'hours' | 'days' | 'weeks';
const UNIT_MINUTES: Record<Unit, number> = { minutes: 1, hours: 60, days: 1440, weeks: 10080 };
const UNIT_ORDER: Unit[] = ['weeks', 'days', 'hours', 'minutes'];

/** The default reminder added by "+ Add reminder": a notification 1 hour before. */
export const DEFAULT_REMINDER: Reminder = { method: 'popup', minutes: 60 };

/** Split minutes into the largest whole unit that divides evenly (mirrors Google's display). */
export function splitMinutes(minutes: number): { value: number; unit: Unit } {
  for (const unit of UNIT_ORDER) {
    const per = UNIT_MINUTES[unit];
    if (minutes >= per && minutes % per === 0) return { value: minutes / per, unit };
  }
  return { value: minutes, unit: 'minutes' };
}

/**
 * A list of editable reminder rows (notification type, amount, unit) each removable, plus an
 * "+ Add reminder" link that appends a default reminder — mirroring how durations are managed
 * and how Google Calendar's reminder editor works.
 */
export function ReminderPicker({
  label,
  hint,
  value,
  onChange,
  maxMinutes = 40320, // 28 days
  className = '',
}: ReminderPickerProps) {
  const update = (i: number, next: Reminder) => onChange(value.map((r, idx) => (idx === i ? next : r)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, { ...DEFAULT_REMINDER }]);

  const setMethod = (i: number, method: ReminderMethod) => update(i, { ...value[i], method });
  const setAmount = (i: number, amount: number, unit: Unit) => {
    if (!Number.isFinite(amount) || amount < 0) return;
    const minutes = Math.min(Math.round(amount) * UNIT_MINUTES[unit], maxMinutes);
    update(i, { ...value[i], minutes });
  };
  const setUnit = (i: number, unit: Unit) => {
    const { value: amount } = splitMinutes(value[i].minutes);
    setAmount(i, amount, unit);
  };

  return (
    <div className={`mb-3 ${className}`.trim()}>
      {label && <label className="form-label d-block">{label}</label>}
      {hint && <div className="form-text mt-0 mb-2">{hint}</div>}

      <div className="d-flex flex-column gap-2">
        {value.map((r, i) => {
          const { value: amount, unit } = splitMinutes(r.minutes);
          return (
            <div key={i} className="d-flex align-items-center gap-2">
              <select
                className="form-select"
                style={{ maxWidth: 150 }}
                value={r.method}
                onChange={(e) => setMethod(i, e.target.value as ReminderMethod)}
                aria-label="Notification type"
              >
                <option value="popup">Notification</option>
                <option value="email">Email</option>
              </select>
              <input
                type="number"
                min={0}
                className="form-control"
                style={{ maxWidth: 90 }}
                value={amount}
                onChange={(e) => setAmount(i, Number(e.target.value), unit)}
                aria-label="Amount"
              />
              <select
                className="form-select"
                style={{ maxWidth: 130 }}
                value={unit}
                onChange={(e) => setUnit(i, e.target.value as Unit)}
                aria-label="Unit"
              >
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
                <option value="days">days</option>
                <option value="weeks">weeks</option>
              </select>
              <button
                type="button"
                className="btn btn-sm btn-icon p-0 border-0 text-muted"
                aria-label="Remove reminder"
                onClick={() => remove(i)}
                style={{ lineHeight: 1 }}
              >
                <i className="ph-x" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-2">
        <button
          type="button"
          className="btn btn-link p-0 text-primary text-decoration-none fw-semibold"
          onClick={add}
        >
          <i className="ph-plus me-1" />
          Add reminder
        </button>
      </div>
    </div>
  );
}
